import PrequalificationHelpers from '../vulcanHelpers/prequalificationHelpers';
import prequalificationSectionHelpers from '../vulcanHelpers/tsbSection/prequalificationSectionHelpers';
import { buildFormFieldsView } from './formFieldsView';
import { fieldProvinceVUL } from '../../utils/constant';

/**
 * The Contentful entry names formStepVulcan matched to hide the service-address
 * section. **The trailing space in `'Service Address '` is significant** — it is
 * the literal entry name the original compared against; `'Adresse de service'`
 * is the French counterpart (no trailing space).
 */
export const SERVICE_ADDRESS_SECTION_NAMES = [
  'Service Address ',
  'Adresse de service'
];

/**
 * Vulcan's IMPERATIVE, name-matched item hides — the `return null` guards
 * `renderFormFields` ran BEFORE its content-type branching, so they applied
 * uniformly to EVERY item: leaf fields, groups, sections and notes alike.
 *
 * The engine covers leaf fields through `fieldRules`, but structural items
 * (`formFieldsGroup` / `formSection` / `formSectionTitle` / `note`) and the
 * content-type sections have no equivalent, so these hides were simply not
 * applied to them — e.g. `tsb15SectionKeys`
 * (existingEquipment/takeoverEquipment*) are SECTION names that must stay hidden
 * unless the rep is on the special outlet AND existing security equipment is
 * "Yes". Confirmed real-data bug: a TSB 1.5 section rendering inside
 * Prequalification when it should not.
 *
 * The original helpers are CALLED here rather than re-implemented — both are
 * pure predicates over `{ fieldName, formFields, salesRepOutlet }`, so reusing
 * them keeps this exactly in step with the app instead of forking the logic.
 * (`additionalCustomerInformationHelpers` is deliberately not called: its body is
 * entirely commented out, so it is a no-op. `CustomerHelpers` /
 * `SecureBusinessHelpers` are also excluded — they MUTATE `formFields`/`fields`
 * and drive option filtering, which is already ported as `fieldRules` for the
 * leaf fields they actually target.)
 *
 * @param {Object} raw   the Contentful entry's `fields` (item.raw)
 * @param {{form?: Object, formFields?: Object, orderData?: Object, salesRepOutlet?: string}} ctx
 *   Supply `formFields` directly when there is no engine `form` to derive it
 *   from (the fieldRules pass, which only receives `values`).
 * @returns {boolean} true when Vulcan hides this item outright
 */
export const isVulcanItemHidden = (raw, ctx = {}) => {
  const fieldName = raw && raw.name;
  if (!fieldName) return false;

  const {
    form,
    formFields: providedFormFields,
    orderData = {},
    salesRepOutlet = ''
  } = ctx;

  // "Hide the service address in the form steps" — once the saved order already
  // carries a province.
  if (
    SERVICE_ADDRESS_SECTION_NAMES.includes(fieldName) &&
    orderData[fieldProvinceVUL]
  ) {
    return true;
  }

  if (PrequalificationHelpers({ fieldName })) return true;

  // Reads other fields' values, so it needs the rich bag shape `getFieldValue`
  // expects (it returns '' for fields that are not visible).
  let formFields = providedFormFields;
  if (!formFields) formFields = form ? buildFormFieldsView(form) : {};
  if (
    prequalificationSectionHelpers({
      fieldName,
      formFields,
      salesRepOutlet,
      orderData
    })
  ) {
    return true;
  }

  return false;
};

export default isVulcanItemHidden;
